import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Callout, Card, Field, SectionHeading, Select, TextArea, TextInput } from '@/components/ui';
import { addDays, formatDate, startOfMonth, todayIso } from '@/domain/dates';
import { formatEur, formatKm, parseAmountToCents } from '@/domain/money';
import type { Invoice, Trip } from '@/domain/types';
import { invoiceRepo, uninvoicedTrips } from '@/db/repositories';
import { useSession } from '@/session/SessionContext';
import { passengerDisplayName } from '@/suggest';
import { useT } from '@/i18n';
import {
  buildDraftInvoice,
  issueInvoice,
  recalculate,
  saveDraft,
  validateInvoice,
  InvoiceIncompleteError,
} from './invoiceService';

export function InvoiceFormScreen() {
  const session = useSession();
  const t = useT();
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();

  const [trips, setTrips] = useState<Trip[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [from, setFrom] = useState(startOfMonth(todayIso()));
  const [to, setTo] = useState(todayIso());
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const passengerMap = useMemo(
    () => new Map(session.passengers.map((p) => [p.id, p])),
    [session.passengers],
  );
  const addressMap = useMemo(
    () => new Map(session.addresses.map((a) => [a.id, a])),
    [session.addresses],
  );

  useEffect(() => {
    void (async () => {
      if (invoiceId) {
        const existing = await invoiceRepo.get(session.dataKey, invoiceId);
        if (existing) {
          setInvoice(existing);
          setSelected(new Set(existing.lines.map((line) => line.tripId).filter(Boolean) as string[]));
        }
      }
      setLoading(false);
    })();
  }, [invoiceId, session.dataKey]);

  useEffect(() => {
    void (async () => setTrips(await uninvoicedTrips(session.dataKey, from, to)))();
  }, [session.dataKey, from, to]);

  const selectedTrips = trips.filter((trip) => selected.has(trip.id));

  function toggle(tripId: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(tripId)) next.delete(tripId);
      else next.add(tripId);
      return next;
    });
  }

  function buildFromSelection() {
    if (selectedTrips.length === 0) {
      setError(t.invoices.errSelectTrip);
      return;
    }
    // Prefill the customer from the passenger if all trips share one, which is
    // the normal case for a monthly corporate invoice.
    const passengerIds = new Set(selectedTrips.map((trip) => trip.passengerId));
    const soleId = passengerIds.size === 1 ? [...passengerIds][0] : undefined;
    const passenger = soleId ? passengerMap.get(soleId) : undefined;

    const draft = buildDraftInvoice({
      company: session.company,
      trips: selectedTrips,
      passengers: passengerMap,
      addresses: addressMap,
      customerPassengerId: soleId,
      customerAddress: passenger?.billingAddressId
        ? formatAddress(addressMap.get(passenger.billingAddressId))
        : '',
    });

    setInvoice(draft);
    setError(null);
  }

  function patch(changes: Partial<Invoice>) {
    setInvoice((current) => (current ? recalculate({ ...current, ...changes }, session.company) : current));
  }

  async function handleSaveDraft() {
    if (!invoice) return;
    setBusy('draft');
    setError(null);
    try {
      const saved = await saveDraft(session.audit, invoice);
      navigate(`/rechnungen/${saved.id}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.common.saveFailed);
    } finally {
      setBusy(null);
    }
  }

  async function handleIssue() {
    if (!invoice) return;
    setBusy('issue');
    setError(null);
    try {
      await saveDraft(session.audit, invoice);
      const issued = await issueInvoice(session.audit, invoice, session.company);
      navigate(`/rechnungen/${issued.id}`);
    } catch (cause) {
      if (cause instanceof InvoiceIncompleteError) {
        setError(cause.problems.map((p) => `${p.message(t)} (${p.legalBasis ?? ''})`).join(' '));
      } else {
        setError(cause instanceof Error ? cause.message : t.invoices.issueFailed);
      }
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <p className="text-muted text-sm">{t.common.loading}</p>;

  const problems = invoice ? validateInvoice(invoice, session.company) : [];

  return (
    <div className="space-y-5">
      <SectionHeading
        title={invoiceId ? t.invoices.formTitleEdit : t.invoices.formTitleNew}
        description={t.invoices.formDescription}
      />

      {!invoice && (
        <>
          <Card title={t.invoices.period}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t.common.from}>
                <TextInput type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
              </Field>
              <Field label={t.common.to}>
                <TextInput type="date" value={to} onChange={(event) => setTo(event.target.value)} />
              </Field>
            </div>
          </Card>

          <Card
            title={t.invoices.uninvoicedTrips(trips.length)}
            action={
              trips.length > 0 && (
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() =>
                    setSelected((current) =>
                      current.size === trips.length
                        ? new Set()
                        : new Set(trips.map((trip) => trip.id)),
                    )
                  }
                >
                  {selected.size === trips.length
                    ? t.invoices.clearSelection
                    : t.invoices.selectAll}
                </button>
              )
            }
          >
            {trips.length === 0 ? (
              <p className="text-muted text-sm">{t.invoices.noOpenTrips}</p>
            ) : (
              <ul className="divide-line -my-2 divide-y">
                {trips.map((trip) => {
                  const passenger = passengerMap.get(trip.passengerId);
                  return (
                    <li key={trip.id}>
                      <label className="flex cursor-pointer items-center gap-3 py-2.5">
                        <input
                          type="checkbox"
                          checked={selected.has(trip.id)}
                          onChange={() => toggle(trip.id)}
                          className="accent-primary h-4 w-4 shrink-0"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="text-heading block truncate text-sm">
                            {passenger ? passengerDisplayName(passenger) : t.common.unknown}
                          </span>
                          <span className="text-muted block truncate text-xs">
                            {formatDate(trip.date)} ·{' '}
                            {addressMap.get(trip.fromAddressId)?.label ?? '?'} →{' '}
                            {addressMap.get(trip.toAddressId)?.label ?? '?'} ·{' '}
                            {formatKm(trip.distanceMeters)} km
                          </span>
                        </span>
                        <span className="text-body shrink-0 text-sm">
                          {formatEur(trip.fareGrossCents)}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          {error && <Callout tone="danger">{error}</Callout>}

          <button
            type="button"
            className="btn-primary"
            onClick={buildFromSelection}
            disabled={selected.size === 0}
          >
            {selected.size === 0
              ? t.invoices.selectTrips
              : t.invoices.takeTrips(selected.size)}
          </button>
        </>
      )}

      {invoice && (
        <>
          <Card title={t.invoices.recipient}>
            <div className="space-y-4">
              <Field label={t.invoices.customerName} required={!invoice.isKleinbetrag}>
                <TextInput
                  value={invoice.customerName}
                  onChange={(event) => patch({ customerName: event.target.value })}
                />
              </Field>
              <Field
                label={t.invoices.customerAddress}
                required={!invoice.isKleinbetrag}
                hint={t.invoices.customerAddressHint}
              >
                <TextArea
                  rows={3}
                  value={invoice.customerAddress}
                  onChange={(event) => patch({ customerAddress: event.target.value })}
                />
              </Field>
              <Field label={t.invoices.customerVatId}>
                <TextInput
                  value={invoice.customerVatId ?? ''}
                  onChange={(event) => patch({ customerVatId: event.target.value || undefined })}
                  placeholder="DE123456789"
                />
              </Field>
            </div>
          </Card>

          <Card title={t.invoices.positionsTitle}>
            <ul className="divide-line divide-y">
              {invoice.lines.map((line, index) => (
                <li key={line.id} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <TextArea
                        rows={2}
                        value={line.description}
                        onChange={(event) =>
                          patch({
                            lines: invoice.lines.map((candidate, position) =>
                              position === index
                                ? { ...candidate, description: event.target.value }
                                : candidate,
                            ),
                          })
                        }
                        className="text-sm"
                      />
                    </div>
                    <div className="w-28 shrink-0">
                      <TextInput
                        value={(line.unitPriceNetCents / 100).toFixed(2).replace('.', ',')}
                        onChange={(event) => {
                          const cents = parseAmountToCents(event.target.value);
                          if (cents === null) return;
                          patch({
                            lines: invoice.lines.map((candidate, position) =>
                              position === index
                                ? { ...candidate, unitPriceNetCents: cents }
                                : candidate,
                            ),
                          });
                        }}
                        inputMode="decimal"
                        className="text-right text-sm"
                      />
                      <p className="text-muted mt-1 text-right text-xs">
                        {t.invoices.netPerUnit(line.vatRatePercent)}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn-ghost shrink-0 px-2 text-xs"
                      onClick={() =>
                        patch({ lines: invoice.lines.filter((_, position) => position !== index) })
                      }
                    >
                      {t.common.remove}
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="border-line mt-4 space-y-1 border-t pt-4 text-sm">
              <Row label={t.invoices.netTotal} value={formatEur(invoice.netTotalCents)} />
              {!invoice.isKleinunternehmer && (
                <Row label={t.invoices.vatTotal} value={formatEur(invoice.vatTotalCents)} />
              )}
              <Row label={t.invoices.grandTotal} value={formatEur(invoice.grossTotalCents)} strong />
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {invoice.isKleinbetrag && <Badge tone="info">{t.invoices.kleinbetragBadge}</Badge>}
              {invoice.isKleinunternehmer && (
                <Badge tone="info">{t.invoices.kleinunternehmerBadge}</Badge>
              )}
              {!invoice.isKleinunternehmer && session.company.licenceType === 'mietwagen' && (
                <Badge>{t.invoices.mietwagenBadge}</Badge>
              )}
            </div>
          </Card>

          <Card title={t.invoices.datesAndText}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t.invoices.issueDate} required>
                <TextInput
                  type="date"
                  value={invoice.issueDate}
                  onChange={(event) =>
                    patch({
                      issueDate: event.target.value,
                      dueDate: addDays(event.target.value, 14),
                    })
                  }
                />
              </Field>
              <Field label={t.invoices.dueDate} required>
                <TextInput
                  type="date"
                  value={invoice.dueDate}
                  onChange={(event) => patch({ dueDate: event.target.value })}
                />
              </Field>
              <Field label={t.invoices.serviceFrom} required hint="§ 14 Abs. 4 Nr. 6 UStG">
                <TextInput
                  type="date"
                  value={invoice.serviceDateFrom}
                  onChange={(event) => patch({ serviceDateFrom: event.target.value })}
                />
              </Field>
              <Field label={t.invoices.serviceTo} required>
                <TextInput
                  type="date"
                  value={invoice.serviceDateTo}
                  onChange={(event) => patch({ serviceDateTo: event.target.value })}
                />
              </Field>
            </div>

            <div className="mt-4 space-y-4">
              <Field label={t.invoices.paymentTerms}>
                <TextInput
                  value={invoice.paymentTerms}
                  onChange={(event) => patch({ paymentTerms: event.target.value })}
                />
              </Field>
              <Field label={t.invoices.notes}>
                <TextArea
                  rows={2}
                  value={invoice.notes ?? ''}
                  onChange={(event) => patch({ notes: event.target.value || undefined })}
                />
              </Field>
              <Field label={t.invoices.eInvoiceField}>
                <Select
                  value={invoice.eInvoiceFormat}
                  onChange={(event) =>
                    patch({ eInvoiceFormat: event.target.value as Invoice['eInvoiceFormat'] })
                  }
                >
                  <option value="keine">{t.invoices.eInvoiceNone}</option>
                  <option value="facturx">{t.invoices.eInvoiceFacturx}</option>
                  <option value="xrechnung-ubl">{t.labels.eInvoice['xrechnung-ubl']}</option>
                  <option value="xrechnung-cii">{t.labels.eInvoice['xrechnung-cii']}</option>
                </Select>
              </Field>
            </div>
          </Card>

          {problems.length > 0 && (
            <Callout tone="warning" title={t.invoices.problemsTitle}>
              <ul className="mt-1 list-disc pl-5">
                {problems.map((problem) => (
                  <li key={`${problem.field}-${problem.legalBasis ?? ''}`}>
                    {problem.message(t)}
                    {problem.legalBasis && (
                      <span className="text-muted"> ({problem.legalBasis})</span>
                    )}
                  </li>
                ))}
              </ul>
            </Callout>
          )}

          {error && <Callout tone="danger">{error}</Callout>}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-primary"
              onClick={() => void handleIssue()}
              disabled={busy !== null || problems.length > 0}
            >
              {busy === 'issue' ? t.invoices.issuing : t.invoices.issue}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void handleSaveDraft()}
              disabled={busy !== null}
            >
              {busy === 'draft' ? t.common.saving : t.invoices.saveDraft}
            </button>
            <button type="button" className="btn-ghost" onClick={() => navigate('/rechnungen')}>
              {t.common.cancel}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className={strong ? 'text-heading font-semibold' : 'text-muted'}>{label}</span>
      <span className={strong ? 'text-heading font-semibold' : 'text-body'}>{value}</span>
    </div>
  );
}

function formatAddress(address: { street?: string; houseNumber?: string; postalCode?: string; city?: string } | undefined): string {
  if (!address) return '';
  return [
    [address.street, address.houseNumber].filter(Boolean).join(' '),
    [address.postalCode, address.city].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join('\n');
}
